import React from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { workflowItems } from "../../pages/workflow";
import { ReactSVG } from "react-svg";
import { operationType } from "../../services/types";
import { consoleURL } from "../../services/API";
import { theme } from "../../styles/theme";

let timeoutId: any = 0;

type templateItemType = {
   id: string;
   title: string;
   description: string;
   category: string;
}

// Single accent used for every active/emphasis state in this panel (rail active
// tab, "View all" links, active filter chip) — matches the toolbar/canvas-controls
// redesign, which deliberately reserves color for one accent instead of a
// per-category palette.
const ACCENT = theme.colors.primary;

// Rail tab icon per category — chrome icons only; the per-block/per-template
// brand icons rendered inside tiles (renderBlockIcon) are left untouched since
// they're a separate, app-wide icon system used for recognizability.
const TAB_ICONS: Record<string, string> = {
   fetch: "ti-plug-connected",
   process: "ti-adjustments",
   actions: "ti-bolt",
   templates: "ti-template",
};

// Soft per-category tint for template card icon badges — keeps each category
// visually distinct without every card competing for attention with a loud color.
const TEMPLATE_CATEGORY_TINTS: Record<string, string> = {
   "E-commerce": "#F59E0B",
   "Support": "#38BDF8",
   "HR": "#A78BFA",
   "Marketing": "#FB7185",
   "Automation": "#34D399",
};
const DEFAULT_TEMPLATE_TINT = "#94A3B8";

export function Sidebar() {
   const [isMiniMenu, setIsMiniMenu] = React.useState<boolean>(true);
   const [isNoWrap, setIsNoWrap] = React.useState<boolean>(false);
   const [search, setSearch] = React.useState<string>('');
   const [draggingAction, setDraggingAction] = React.useState<string | null>(null);
   const [categories, setCategories] = React.useState<{ name: string, ref: string, image: string, isToggle?: boolean }[]>([
      { name: 'Fetch Data', ref: 'fetch', image: 'images/fetch1.png', isToggle: true },
      { name: 'Process', ref: 'process', image: 'images/process1.png' },
      { name: 'Actions', ref: 'actions', image: 'images/actions1.png' },
      { name: 'Template', ref: 'templates', image: 'images/templates1.png' },
   ]);
   const [items, setItems] = React.useState<{ name: string, isToggle: boolean, data: operationType[] }[]>([]);
   const [templateItems, setTemplateItems] = React.useState<templateItemType[]>([]);
   const [templateCategory, setTemplateCategory] = React.useState<string>('');
   const [isTemplatesLoading/* , setIsTemplatesLoading */] = React.useState<boolean>(false);

   // ---
   React.useEffect(() => {
      const category = categories.find(category => category.isToggle)?.name;
      const filter = workflowItems.filter(item => item.category?.includes(category as string));

      setItems(Object.values(filter.reduce((acc, item) => { // @ts-ignore
         const subCategory: any = item.subCategory[category];
         if (subCategory && !item.isHide) { // @ts-ignore
            if (!acc[subCategory]) acc[subCategory] = { name: subCategory, isToggle: false, data: [] }; // @ts-ignore
            acc[subCategory].data.push(item);
         }
         return acc;
      }, {})));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [categories]);

   React.useEffect(() => {
      const handleMessage = (event: any) => {
         if (event.origin === consoleURL && event.data.type === 'TEMPLATES_DATA') {
            setTemplateItems(event.data.templates);
         }
      }
      window.addEventListener("message", handleMessage);
      return () => { window.removeEventListener("message", handleMessage) };
   }, []);

   // ---
   React.useEffect(() => {
      clearTimeout(timeoutId);
      setIsNoWrap(true);
      timeoutId = setTimeout(() => { setIsNoWrap(false) }, 500);
      return () => {
         clearTimeout(timeoutId);
      }
   }, [isMiniMenu]);

   // Publish sidebar geometry so canvas chrome (toolbar / zoom / chips) can
   // stick to the panel edge and re-center when it expands or collapses.
   React.useEffect(() => {
      const root = document.documentElement;
      const width = isMiniMenu ? 72 : 410;
      const end = 18 + width;
      root.style.setProperty('--wf-sidebar-end', `${end}px`);
      root.dataset.wfSidebar = isMiniMenu ? 'collapsed' : 'expanded';
      return () => {
         root.style.removeProperty('--wf-sidebar-end');
         delete root.dataset.wfSidebar;
      };
   }, [isMiniMenu]);

   // ---
   const onDragStart = (event: React.DragEvent<HTMLDivElement>, action: string) => {
      setDraggingAction(action);
      event.dataTransfer.setData('text/plain', categories.find(c => c.isToggle)?.name as string);
      event.dataTransfer.setData('application/reactflow', action);
      event.dataTransfer.effectAllowed = 'move';
   }
   const onDragEnd = () => setDraggingAction(null);

   // ---
   const onTemplateDragStart = (event: React.DragEvent<HTMLDivElement>, template: templateItemType) => {
      event.dataTransfer.setData('application/reactflow/template-title', template.title);
      event.dataTransfer.setData('text/plain', template.id);
      event.dataTransfer.setData('application/reactflow', 'template');
      event.dataTransfer.effectAllowed = 'move';
   }

   // ---
   const categoryToggle = (name: string) => {
      setItems((items) =>
         items.map((item) => {
            if (item.name === name) return { ...item, isToggle: !item.isToggle };
            else return item;
         })
      );
   }

   // ---
   const categoryActive = categories.find(category => category.isToggle);
   const filterWithSearch = React.useMemo(() => workflowItems.filter(item => !item.isHide && item.name.toLowerCase().includes(search.toLowerCase())), [search]);
   const templateCategories = React.useMemo(() => {
      const counts = new Map<string, number>();
      for (const item of templateItems) counts.set(item.category, (counts.get(item.category) || 0) + 1);
      return Array.from(counts.keys()).sort((a, b) => (counts.get(b) || 0) - (counts.get(a) || 0));
   }, [templateItems]);
   const filteredTemplateItems = React.useMemo(() => {
      // if (templateCategory === 'All') return templateItems;
      return templateItems.filter(item => item.category === templateCategory);
   }, [templateCategory, templateItems]);

   // Activate the category with the most templates by default.
   React.useEffect(() => {
      if (templateCategories.length && !templateCategories.includes(templateCategory)) {
         setTemplateCategory(templateCategories[0]);
      }
   }, [templateCategories, templateCategory]);

   // ---
   const renderBlockIcon = (block: operationType) => (
      typeof block.icon === 'string'
         ? (block.icon.endsWith('.png') || block.icon.endsWith('.jpg')
            ? <img src={block.icon} alt={block.name} style={{ width: 20, height: 20, objectFit: 'contain' }} />
            : <ReactSVG className="otherSvg" src={block.icon} />)
         : <FontAwesomeIcon icon={block.icon} />
   );

   return (
      <Main className={isMiniMenu ? 'minimizeMenu' : ''}>
         <div className="logo">
            {!isMiniMenu ? <div className="title">
               <div className="icon">
                  <i className="ti ti-hierarchy-3" aria-hidden="true" />
               </div>
               <div className="info">
                  <h4>Workflow builder</h4>
                  <span>Design and automate</span>
               </div>
            </div> : null}
            <div className="mini" onClick={() => setIsMiniMenu(isToggle => !isToggle)} role="button" aria-label={isMiniMenu ? 'Expand sidebar' : 'Collapse sidebar'}>
               <i className={ `ti ${ isMiniMenu ? 'ti-layout-sidebar-left-expand' : 'ti-layout-sidebar-left-collapse' }` } aria-hidden="true" />
            </div>
         </div>
         <div className="search">
            <i className="ti ti-search" aria-hidden="true" onClick={_ => { if (isMiniMenu) setIsMiniMenu(false) }} />
            <input
               placeholder={isMiniMenu ? "" : "Search blocks…"}
               value={search}
               onChange={e => setSearch(e.target.value)}
               onClick={_ => { if (isMiniMenu) setIsMiniMenu(false) }}
               aria-label="Search blocks"
            />
         </div>
         <div className="categories">{
            !search ? <>
               <ul>{
                  categories.map((item, index) => (
                     <li
                        key={item.name}
                        className={`${item.ref} ${item.isToggle ? 'active' : ''}`}
                        onClick={_ => {
                           setCategories(prev => prev.map((p, i) => ({ ...p, isToggle: i === index })));
                           if (isMiniMenu) setIsMiniMenu(false);
                        }}
                     >
                        <span className="iconWrap">
                           <i className={ `ti ${ TAB_ICONS[item.ref] || 'ti-square' }` } aria-hidden="true" />
                        </span>
                        <span className="label">{item.ref === 'fetch' ? 'Fetch' : item.name}</span>
                     </li>
                  ))
               }</ul>
               <div className="blocks">
                  <div className="container">{
                     categoryActive?.ref === 'templates' ? (
                        <div className="templatesContent">
                           {isTemplatesLoading ? (
                              <div className="templateLoading">{
                                 [1, 2, 3].map(item => (
                                    <div key={item}>
                                       <span />
                                       <div>
                                          <strong />
                                          <p />
                                       </div>
                                    </div>
                                 ))
                              }</div>
                           ) : templateItems.length === 0 ? (
                              <div className="emptyState">
                                 <i className="ti ti-template" aria-hidden="true" style={{ fontSize: 20 }} />
                                 <span>No templates available yet</span>
                              </div>
                           ) : <>
                              <div className="templateFilters">{
                                 templateCategories.map(category => (
                                    <button
                                       key={category}
                                       className={templateCategory === category ? 'active' : ''}
                                       onClick={_ => setTemplateCategory(category)}
                                    >
                                       {category}
                                    </button>
                                 ))
                              }</div>
                              <div className="templateList">{
                                 filteredTemplateItems.map(template => (
                                    <div
                                       key={template.id}
                                       onDragStart={event => onTemplateDragStart(event, template)}
                                       onDragEnd={onDragEnd}
                                       draggable
                                       title={template.description}
                                       style={{ ['--tint' as any]: TEMPLATE_CATEGORY_TINTS[template.category] || DEFAULT_TEMPLATE_TINT }}
                                    >
                                       <span className="iconWrap"><i className="ti ti-template" aria-hidden="true" /></span>
                                       <div>
                                          <h4>{template.title}</h4>
                                          <p>{template.description}</p>
                                       </div>
                                    </div>
                                 ))
                              }</div>
                           </>}
                        </div>
                     ) : items.map(item => (
                        <div key={item.name} className={`items ${isNoWrap ? 'noWrap' : ''}`}>
                           <div className="top">
                              <h4>{item.name}</h4>
                              {item.data.length > 3 ? <span onClick={_ => categoryToggle(item.name)}>
                                 {item.isToggle ? 'Less' : 'View all'}
                                 <i className={ `ti ti-chevron-down ${ item.isToggle ? 'flip' : '' }` } aria-hidden="true" />
                              </span> : null}
                           </div>
                           <div className="grid">{
                              item.data.slice(0, item.isToggle ? item.data.length : 3).map(block => (
                                 <div
                                    key={block.action}
                                    onDragStart={event => onDragStart(event, block.action)}
                                    onDragEnd={onDragEnd}
                                    draggable
                                    title={`Drag "${block.name}" onto the canvas`}
                                    className={draggingAction === block.action ? 'dragging' : ''}
                                 >
                                    <span className="iconWrap" style={{ backgroundColor: block.background || theme.colors.primary }}>
                                       {renderBlockIcon(block)}
                                    </span>
                                    <span className="name">{block.name}</span>
                                 </div>
                              ))
                           }</div>
                        </div>
                     ))
                  }</div>
               </div>
            </> : <div className="items searchFilter"><div>{
               filterWithSearch.length === 0 ? (
                  <div className="emptyState fullWidth">
                     <i className="ti ti-search" aria-hidden="true" style={{ fontSize: 18 }} />
                     <span>No blocks match &quot;{search}&quot;</span>
                  </div>
               ) : filterWithSearch.map(block => (
                  <div
                     key={block.action}
                     onDragStart={event => onDragStart(event, block.action)}
                     onDragEnd={_ => { setSearch(''); onDragEnd(); }}
                     draggable
                     className={draggingAction === block.action ? 'dragging' : ''}
                  >
                     <span className="iconWrap" style={{ backgroundColor: block.background || theme.colors.primary }}>
                        {renderBlockIcon(block)}
                     </span>
                     <span className="name">{block.name}</span>
                  </div>
               ))
            }</div></div>
         } </div>
      </Main>
   );
}

const Main = styled.nav`
   --panel: #0B0820;
   --panel-2: #16103A;
   --panel-3: #1E1458;
   --line: rgba(255, 255, 255, 0.08);
   --line-strong: rgba(255, 255, 255, 0.14);
   --text-hi: #F4F2FF;
   --text-mid: #A8A3C6;
   --text-lo: #6E6890;
   --accent: ${ACCENT};
   --accent-bright: ${theme.colors.primaryHover};
   --ink-700: rgba(255, 255, 255, 0.03);
   --ink-600: rgba(255, 255, 255, 0.055);

   position: absolute;
   top: 18px;
   left: 18px;
   bottom: 18px;
   z-index: 40;
   display: flex;
   flex-direction: column;
   width: 410px;
   min-width: 0;
   height: auto;
   max-height: none;
   margin: 0;
   overflow: hidden;
   isolation: isolate;
   background:
      radial-gradient(ellipse 90% 55% at 8% -5%, rgba(129, 114, 255, 0.38), transparent 55%),
      radial-gradient(ellipse 70% 45% at 100% 12%, rgba(79, 63, 224, 0.28), transparent 50%),
      radial-gradient(ellipse 80% 50% at 50% 110%, rgba(42, 32, 112, 0.55), transparent 55%),
      radial-gradient(circle at 70% 60%, rgba(84, 224, 208, 0.06), transparent 40%),
      linear-gradient(165deg, var(--panel-3) 0%, var(--panel-2) 42%, var(--panel) 100%);
   border: 1px solid rgba(167, 152, 255, 0.22);
   border-radius: 22px;
   box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.1) inset,
      0 0 0 1px rgba(0, 0, 0, 0.35) inset,
      0 28px 56px -18px rgba(8, 5, 28, 0.72),
      0 10px 24px -12px rgba(79, 63, 224, 0.35);
   color: var(--text-hi);
   font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
   transition: width 0.32s cubic-bezier(0.2, 0.9, 0.25, 1), min-width 0.32s cubic-bezier(0.2, 0.9, 0.25, 1);
   scrollbar-width: none;
   -ms-overflow-style: none;
   &::-webkit-scrollbar { width: 0; height: 0; }

   &::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 0;
      pointer-events: none;
      border-radius: inherit;
      background-image:
         linear-gradient(rgba(255, 255, 255, 0.028) 1px, transparent 1px),
         linear-gradient(90deg, rgba(255, 255, 255, 0.028) 1px, transparent 1px);
      background-size: 22px 22px;
      mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.12) 55%, transparent 100%);
      -webkit-mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.12) 55%, transparent 100%);
      opacity: 0.9;
   }

   &::after {
      content: '';
      position: absolute;
      top: -20%;
      right: -30%;
      width: 70%;
      height: 55%;
      z-index: 0;
      pointer-events: none;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(129, 114, 255, 0.22) 0%, transparent 68%);
      filter: blur(2px);
   }

   > * {
      position: relative;
      z-index: 1;
   }

   &.minimizeMenu {
      min-width: 0;
      width: 72px;

      .logo {
         flex-direction: column;
         gap: 10px;
         padding: 14px 8px 10px;
         margin: 0;
         justify-content: center;

         .title {
            flex-direction: column;

            .info {
               display: none;
            }
            .icon {
               margin-right: 0;
               min-width: 40px;
               width: 40px;
               height: 40px;
               border-radius: 12px;

               i.ti {
                  font-size: 18px;
               }
            }
         }
         .mini {
            width: 34px;
            height: 34px;
            border-radius: 10px;

            i.ti {
               font-size: 15px;
            }
         }
      }
      .search {
         display: none;
      }
      .categories {
         ul:first-of-type {
            width: 100%;
            min-width: 0;
            border-right: none;
            padding: 10px 8px;
            gap: 6px;

            li {
               padding: 4px 0;

               .iconWrap {
                  width: 40px;
                  height: 40px;
                  border-radius: 12px;

                  i.ti {
                     font-size: 18px;
                  }
               }
            }
         }
         .blocks {
            display: none;
         }
         li .label {
            display: none;
         }
      }
   }

   .logo {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 0;
      padding: 20px 20px 16px;
      transition: all 0.2s ease;

      .title {
         display: flex;
         align-items: center;
         cursor: default;
         min-width: 0;
         gap: 12px;

         .icon {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 44px;
            width: 44px;
            height: 44px;
            background: linear-gradient(150deg, var(--accent-bright), var(--accent) 75%);
            color: #fff;
            border-radius: 13px;
            margin-right: 0;
            box-shadow:
               0 1px 0 rgba(255, 255, 255, 0.3) inset,
               0 -4px 8px rgba(0, 0, 0, 0.25) inset,
               0 12px 22px -8px rgba(79, 63, 224, 0.6);

            i.ti {
               font-size: 20px;
            }
         }
         .info {
            white-space: nowrap;
            min-width: 0;

            h4 {
               margin: 0;
               font-family: 'Space Grotesk', Inter, system-ui, sans-serif;
               font-size: 16.5px;
               font-weight: 700;
               letter-spacing: -0.01em;
               color: var(--text-hi);
            }
            span {
               display: block;
               margin-top: 2px;
               font-size: 12px;
               font-weight: 500;
               color: var(--text-lo);
            }
         }
      }
      .mini {
         display: flex;
         align-items: center;
         justify-content: center;
         width: 34px;
         height: 34px;
         flex-shrink: 0;
         cursor: pointer;
         transition: background-color 150ms ease, color 150ms ease, border-color 150ms ease, box-shadow 150ms ease;
         color: var(--text-mid);
         border: 1px solid var(--line-strong);
         border-radius: 10px;
         background: rgba(255, 255, 255, 0.03);

         i.ti {
            font-size: 15px;
         }
         &:hover {
            color: #fff;
            border-color: var(--accent-bright);
            box-shadow: 0 0 0 3px rgba(129, 114, 255, 0.15);
         }
         &:focus-visible {
            outline: 2px solid var(--accent);
            outline-offset: 2px;
         }
      }
   }

   .search {
      position: relative;
      display: flex;
      align-items: center;
      padding: 0 20px 16px;

      input {
         width: 100%;
         height: 42px;
         border-radius: 12px;
         border: 1px solid rgba(167, 152, 255, 0.18);
         background: rgba(12, 8, 36, 0.42);
         padding: 0 14px 0 40px;
         color: var(--text-hi);
         font-size: 13.5px;
         transition: background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease;
         backdrop-filter: blur(10px);

         &::placeholder {
            color: var(--text-lo);
         }
         &:hover {
            background: rgba(255, 255, 255, 0.05);
         }
         &:focus {
            outline: none;
            background: rgba(255, 255, 255, 0.06);
            border-color: var(--accent-bright);
            box-shadow: 0 0 0 3px rgba(129, 114, 255, 0.16);
         }
         &::-webkit-search-cancel-button,
         &::-webkit-search-decoration,
         &::-webkit-search-results-button,
         &::-webkit-search-results-decoration {
            display: none;
         }
      }
      i.ti {
         position: absolute;
         left: 34px;
         color: var(--text-lo);
         font-size: 15px;
         transition: color 150ms ease;
         pointer-events: none;
      }
      &:focus-within i.ti {
         color: var(--accent-bright);
      }
   }

   .categories {
      display: flex;
      flex: 1;
      min-height: 0;
      border-top: 1px solid var(--line);

      ul:first-of-type {
         display: flex;
         flex-direction: column;
         align-items: center;
         flex-shrink: 0;
         width: 76px;
         min-width: 76px;
         height: 100%;
         padding: 16px 10px;
         gap: 6px;
         border-right: 1px solid var(--line);

         li {
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 8px;
            width: 100%;
            padding: 9px 4px 10px;
            color: var(--text-lo);
            font-size: 10.5px;
            font-weight: 600;
            letter-spacing: 0.01em;
            cursor: pointer;
            border-radius: 14px;
            border: none;
            text-align: center;
            transition: background-color 150ms ease, color 150ms ease;

            .iconWrap {
               display: flex;
               align-items: center;
               justify-content: center;
               width: 38px;
               height: 38px;
               border-radius: 11px;
               background: rgba(255, 255, 255, 0.04);
               border: 1px solid var(--line-strong);
               transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

               i.ti {
                  font-size: 18px;
                  color: inherit;
                  transition: color 150ms ease;
               }
            }

            .label {
               white-space: nowrap;
            }

            &:hover {
               color: var(--text-mid);

               .iconWrap {
                  background: rgba(255, 255, 255, 0.08);
               }
            }
            &.active {
               color: var(--accent-bright);

               .label {
                  font-weight: 700;
               }
               .iconWrap {
                  background: linear-gradient(150deg, var(--accent-bright), var(--accent));
                  border-color: transparent;
                  color: #fff;
                  box-shadow:
                     0 1px 0 rgba(255, 255, 255, 0.3) inset,
                     0 -3px 6px rgba(0, 0, 0, 0.2) inset,
                     0 10px 20px -8px rgba(129, 114, 255, 0.65);

                  i.ti {
                     color: #fff;
                  }
               }
            }
            &:focus-visible {
               outline: 2px solid var(--accent);
               outline-offset: 2px;
            }
         }
      }

      .blocks {
         display: flex;
         flex-direction: column;
         flex: 1;
         min-width: 0;
         min-height: 0;
         height: 100%;
         color: var(--text-mid);

         .container {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            overflow-x: hidden;
            margin: 0;
            padding: 18px 18px 24px;
            box-sizing: border-box;
            scrollbar-width: thin;
            scrollbar-color: rgba(255, 255, 255, 0.12) transparent;

            &::-webkit-scrollbar {
               width: 6px;
            }
            &::-webkit-scrollbar-thumb {
               background: rgba(255, 255, 255, 0.12);
               border-radius: 999px;
            }
         }
      }

      .emptyState {
         display: flex;
         flex-direction: column;
         align-items: center;
         gap: 8px;
         padding: 28px 10px;
         text-align: center;
         color: var(--text-lo);
         font-size: 12.5px;

         svg, i {
            opacity: 0.7;
         }
         &.fullWidth {
            grid-column: 1 / -1;
         }
      }

      .templatesContent {
         display: flex;
         flex-direction: column;
         gap: 0;
         min-height: 0;
         height: 100%;
      }
      .templateFilters {
         display: flex;
         flex-wrap: wrap;
         gap: 8px;
         padding: 0 0 14px;
         flex-shrink: 0;

         button {
            min-width: 0;
            padding: 8px 15px;
            color: var(--text-mid);
            font-size: 12px;
            font-weight: 700;
            background: rgba(255, 255, 255, 0.04);
            border: 1px solid var(--line-strong);
            border-radius: 999px;
            cursor: pointer;
            transition: color 150ms ease, border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease;
            white-space: nowrap;

            &.active {
               color: #fff;
               background: linear-gradient(150deg, var(--accent-bright), var(--accent));
               border-color: transparent;
               box-shadow:
                  0 1px 0 rgba(255, 255, 255, 0.3) inset,
                  0 10px 20px -8px rgba(79, 63, 224, 0.55);
            }
            &:hover:not(.active) {
               color: var(--text-hi);
               background: rgba(255, 255, 255, 0.08);
            }
         }
      }
      .templateLoading {
         display: flex;
         flex-direction: column;
         gap: 10px;

         > div {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            width: 100%;
            padding: 13px 14px;
            overflow: hidden;
            border: 1px solid var(--line-strong);
            border-radius: 15px;
            background: rgba(255, 255, 255, 0.03);

            span,
            strong,
            p {
               display: block;
               background: linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.1), rgba(255,255,255,0.04));
               background-size: 200% 100%;
               animation: templateLoading 1s linear infinite;
            }
            span {
               flex: 0 0 36px;
               width: 36px;
               height: 36px;
               border-radius: 10px;
            }
            > div {
               width: 100%;
               min-width: 0;
            }
            strong {
               width: 70%;
               height: 13px;
               margin-bottom: 7px;
               border-radius: 4px;
               display: block;
            }
            p {
               width: 100%;
               height: 11px;
               border-radius: 4px;
            }
         }
      }
      .templateList {
         display: flex;
         flex-direction: column;
         gap: 10px;

         > div {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            width: 100%;
            padding: 13px 14px;
            overflow: hidden;
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid var(--line-strong);
            border-radius: 15px;
            transition: background-color 150ms ease, border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease;

            .iconWrap {
               display: flex;
               align-items: center;
               justify-content: center;
               flex: 0 0 36px;
               width: 36px;
               height: 36px;
               color: #fff;
               background: linear-gradient(150deg, #F2A84E, #C97A1F);
               border-radius: 10px;
               box-shadow:
                  0 1px 0 rgba(255, 255, 255, 0.28) inset,
                  0 8px 16px -6px rgba(0, 0, 0, 0.4);

               i.ti {
                  font-size: 16px;
               }
            }
            > div {
               width: 100%;
               min-width: 0;
            }
            h4 {
               margin: 0 0 4px;
               color: var(--text-hi);
               font-size: 13px;
               font-weight: 700;
               line-height: 1.35;
            }
            p {
               margin: 0;
               color: var(--text-lo);
               font-size: 11.5px;
               line-height: 1.45;
               overflow: hidden;
               text-overflow: ellipsis;
               display: -webkit-box;
               -webkit-line-clamp: 1;
               -webkit-box-orient: vertical;
               white-space: normal;
            }
            &:hover {
               background: rgba(255, 255, 255, 0.06);
               border-color: rgba(129, 114, 255, 0.4);
               transform: translateX(3px);
               box-shadow:
                  0 14px 26px -14px rgba(0, 0, 0, 0.55),
                  0 0 0 1px rgba(129, 114, 255, 0.12);
               cursor: grab;
            }
         }
      }

      .items {
         width: 100%;
         position: relative;

         &.searchFilter {
            padding: 18px;
            overflow-y: auto;
            overflow-x: hidden;
            width: 100%;
            scrollbar-width: thin;

            > div {
               display: grid;
               grid-template-columns: repeat(3, 1fr);
               gap: 8px;
               justify-content: initial;

               > div {
                  width: 100%;
                  margin: 0;
               }
            }
         }
         &.noWrap {
            opacity: 0.65;
         }
         &:not(:last-of-type) {
            margin-bottom: 22px;
         }

         .top {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 12px;

            h4 {
               margin: 0;
               color: var(--text-mid);
               white-space: nowrap;
               font-size: 12.5px;
               font-weight: 700;
            }
            span {
               display: flex;
               align-items: center;
               gap: 4px;
               font-size: 11.5px;
               font-weight: 600;
               color: var(--accent-bright);
               cursor: pointer;
               transition: opacity 150ms ease;

               i.ti {
                  font-size: 11px;
                  transition: transform 150ms ease;
                  &.flip {
                     transform: rotate(180deg);
                  }
               }
               &:hover {
                  opacity: 0.8;
               }
            }
         }

         .grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;

            > div {
               position: relative;
               display: flex;
               flex-direction: column;
               align-items: center;
               justify-content: center;
               gap: 5px;
               aspect-ratio: 1;
               min-width: 0;
               min-height: 0;
               padding: 8px 6px;
               background: rgba(255, 255, 255, 0.045);
               border: 1px solid rgba(167, 152, 255, 0.14);
               border-radius: 14px;
               cursor: grab;
               transition: all 0.18s ease;
               text-align: center;
               box-sizing: border-box;
               backdrop-filter: blur(8px);

               .iconWrap {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  flex-shrink: 0;
                  width: 42px;
                  height: 42px;
                  border-radius: 11px;
                  box-shadow:
                     0 1px 0 rgba(255, 255, 255, 0.25) inset,
                     0 8px 16px -6px rgba(0, 0, 0, 0.4);

                  .otherSvg {
                     width: 20px;
                     height: 20px;
                     display: flex;

                     svg {
                        fill: #fff;
                        position: relative;
                        path {
                           fill: #fff;
                        }
                     }
                  }
                  svg {
                     width: 20px;
                     height: 20px;
                     color: #fff;
                  }
                  img {
                     width: 20px !important;
                     height: 20px !important;
                  }
               }
               .name {
                  width: 100%;
                  font-size: 9.5px;
                  font-weight: 600;
                  color: var(--text-hi);
                  text-align: center;
                  line-height: 1.15;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
               }

               &:hover,
               &:focus-visible {
                  background: rgba(255, 255, 255, 0.055);
                  transform: translateY(-3px);
                  border-color: rgba(129, 114, 255, 0.4);
                  box-shadow:
                     0 14px 26px -12px rgba(0, 0, 0, 0.55),
                     0 0 0 1px rgba(129, 114, 255, 0.15);
               }
               &.dragging {
                  opacity: 0.45;
               }
               &:focus-visible {
                  outline: 2px solid var(--accent);
                  outline-offset: 2px;
               }
            }
         }

         &.searchFilter > div > div {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 5px;
            aspect-ratio: 1;
            margin: 0;
            padding: 8px 6px;
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid var(--line-strong);
            border-radius: 14px;
            cursor: grab;
            transition: all 0.18s ease;
            box-sizing: border-box;

            .iconWrap {
               display: flex;
               align-items: center;
               justify-content: center;
               width: 42px;
               height: 42px;
               border-radius: 11px;

               .otherSvg {
                  width: 20px;
                  height: 20px;
                  svg {
                     fill: #fff;
                     path { fill: #fff; }
                  }
               }
               svg {
                  width: 20px;
                  height: 20px;
                  color: #fff;
               }
               img {
                  width: 20px !important;
                  height: 20px !important;
               }
            }
            .name {
               text-align: center;
               font-size: 9.5px;
               font-weight: 600;
               color: var(--text-hi);
               line-height: 1.15;
            }
            &:hover {
               background: rgba(255, 255, 255, 0.055);
               transform: translateY(-3px);
               border-color: rgba(129, 114, 255, 0.4);
            }
            &.dragging {
               opacity: 0.45;
            }
         }
      }
   }

   @keyframes templateLoading {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
   }

   @media (prefers-reduced-motion: reduce) {
      * { transition-duration: 0.01ms !important; }
   }
`;



